import React, { useState, useMemo, useRef, useEffect } from "react"
import { useClientsSearch } from "@/hooks/use-clients-search"
import { InternationalShipmentIdsEditor } from "@/components/international-shipment-ids-editor"
import { useMobileEditor, MobileEditorPanel, MobileEditorConfirmDialog } from "@/hooks/use-mobile-editor"
import type { MobileOptionBase } from "@/hooks/use-mobile-editor"
import type { SearchableOption } from "@/components/ui/searchable-select"
import type {
  ShipmentStatus, WorkLine, ProductRow, ShipmentDetail, ShipmentDocRow,
  ClientOption, CargoTypeOption, PartyOption, RouteOption, TripStatus, TripRow,
  CityOption, TripFormState, FormState, ValorizationType, ValorizationRow,
  ValorizationFormState, JourneyMilestone, ShipmentDocTypeOption,
} from "./ShipmentDetailTypes"
import {
  TRIP_STATUS_LABELS, TRIP_STATUS_COLORS, ALL_TRIP_STATUSES,
  EMPTY_TRIP_FORM, EMPTY_FORM, VALORIZATION_TYPE_LABELS, VALORIZATION_TYPE_VALUES,
  EMPTY_VALORIZATION_FORM, STATUS_LABELS, STATUS_COLORS, WORK_LINE_LABELS,
  WORK_LINE_COLORS, ALL_STATUSES, EDITABLE_STATUSES, JOURNEY_MILESTONES, CANCEL_REASON_LABELS,
} from "./ShipmentDetailTypes"
import {
  fmtDate, fmtDateLong, fmtCargoValue, isoToLocalDatetime,
  fmtTripPlanDate, fmtTripPlanShort,
  DELIVERY_NOTE_STATUS_LABEL, DELIVERY_NOTE_STATUS_CLASS,
  type DeliveryNoteStatus,
} from "./ShipmentDetailUtils"
import {
  Field, SectionCard, TechEquipmentSection, TechCheckModal,
  EquipmentSection, WeatherDaySection, TripPlanModal, JourneyTimeline,
  DocRow, FlagBadge,
} from "./ShipmentDetailComponents"
import { ShipmentTripsTable } from "./ShipmentTripsTable"
import { ShipmentRouteInfoBlock } from "@/components/shipment-route-info-block"
import { NationalRouteEditor } from "./NationalRouteEditor"
import { syncNationalRouteStops, type NationalRouteStop } from "@/lib/national-route"
import { buildShipmentPayload as buildPayload } from "@/lib/shipment-payload"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { SiteHeader } from "@/components/site-header"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { usePermissions } from "@/hooks/use-permissions"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { SearchableSelect } from "@/components/ui/searchable-select"
import { normalize as normalizeTravelAdvance } from "@/lib/travel-advance"
import { nextInternationalId } from "@cdc-sccr/shared"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ArrowLeftIcon,
  PencilIcon,
  Trash2Icon,
  CheckCircle2Icon,
  XCircleIcon,
  PackageIcon,
  MapPinIcon,
  ClockIcon,
  TruckIcon,
  FileTextIcon,
  RotateCcwIcon as RestoreIcon,
  ArrowRightIcon,
  PlusIcon,
  ScissorsIcon,
  MapIcon,
  CalendarIcon,
  MoonIcon,
  ShieldCheckIcon,
  NavigationIcon,
  ClipboardCheckIcon,
  CheckIcon,
  AlertTriangleIcon,
  DollarSignIcon,
  UploadIcon,
  FileIcon,
  ExternalLinkIcon,
  CloudSunIcon,
  BanIcon,
  EyeIcon,
  RouteIcon,
  GitBranchIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { avatarInitials, avatarColor } from "@/lib/avatar"
import { BATEA_LOADING_WINDOWS, DEFAULT_SHIPMENT_DOC_TYPE_CODE, INTERNACIONAL_STAGES, analyzeSplitImpact } from "@cdc-sccr/shared"

// MobileOption is a local alias for the canonical MobileOptionBase from the hook
type MobileOption = MobileOptionBase

// ─── (Types, constants, helpers and sub-components are in ShipmentDetailTypes.ts,
//      ShipmentDetailUtils.ts and ShipmentDetailComponents.tsx) ────────────────


// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ShipmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { can, role } = usePermissions()
  const isAdminOrOperator = can("write", "shipments") || role === "operator"

  const [isEditing,              setIsEditing]              = useState(false)
  const [deleteOpen,             setDeleteOpen]             = useState(false)
  const [deleteTechCheckOpen,    setDeleteTechCheckOpen]    = useState(false)
  const [deleteTripPlanOpen,     setDeleteTripPlanOpen]     = useState(false)
  const [form,          setForm]          = useState<FormState>({ ...EMPTY_FORM })
  const [formError,     setFormError]     = useState<string | null>(null)
  const nextInternationalIdQuery = useQuery<{ corridor: string | null; seed: string | null; next: string | null }>({
    queryKey: ["shipment-detail-international-id-next", form.senderId, form.recipientId],
    enabled: form.workLine === "internacional" && Boolean(form.senderId && form.recipientId),
    queryFn: async () => {
      const params = new URLSearchParams({ senderId: form.senderId, recipientId: form.recipientId })
      const res = await authorizedFetch(`${API_URL}/api/shipments/international-ids/next?${params}`)
      return res.json() as Promise<{ corridor: string | null; seed: string | null; next: string | null }>
    },
    staleTime: 0,
  })
  // Trip state
  const [tripSheetOpen,   setTripSheetOpen]   = useState(false)
  const [unitSearch,      setUnitSearch]      = useState("")
  const [unitSearchDebounced, setUnitSearchDebounced] = useState("")

  useEffect(() => {
    const t = setTimeout(() => setUnitSearchDebounced(unitSearch), 300)
    return () => clearTimeout(t)
  }, [unitSearch])
  const [editingTrip,     setEditingTrip]     = useState<TripRow | null>(null)
  const [tripForm,        setTripForm]        = useState<TripFormState>({ ...EMPTY_TRIP_FORM })
  const [tripFormError,   setTripFormError]   = useState<string | null>(null)
  const [editResources,   setEditResources]   = useState(false)
  const [deletingTrip,  setDeletingTrip]  = useState<TripRow | null>(null)
  const [tripDeleteOpen,setTripDeleteOpen]= useState(false)
  // Split trip state
  const [splitingTrip,  setSplitingTrip]  = useState<TripRow | null>(null)
  const [splitOpen,     setSplitOpen]     = useState(false)
  const [splitMobileId, setSplitMobileId] = useState("")
  const [splitCityId,   setSplitCityId]   = useState("")
  const [splitDriverId, setSplitDriverId] = useState("")
  const [splitTruckId,  setSplitTruckId]  = useState("")
  const [splitTrailerId,setSplitTrailerId]= useState("")
  const [splitTruckSearch,   setSplitTruckSearch]   = useState("")
  const [splitTrailerSearch, setSplitTrailerSearch] = useState("")
  const [splitTruckSearchDb,   setSplitTruckSearchDb]   = useState("")
  const [splitTrailerSearchDb, setSplitTrailerSearchDb] = useState("")

  useEffect(() => {
    const t = setTimeout(() => setSplitTruckSearchDb(splitTruckSearch), 300)
    return () => clearTimeout(t)
  }, [splitTruckSearch])

  useEffect(() => {
    const t = setTimeout(() => setSplitTrailerSearchDb(splitTrailerSearch), 300)
    return () => clearTimeout(t)
  }, [splitTrailerSearch])
  // Trip plan modal
  const [tripPlanOpen,   setTripPlanOpen]   = useState(false)
  const [techCheckOpen,  setTechCheckOpen]  = useState(false)

  // Tech-check entry detail popup (multi-day format)
  const [techCheckEntryDetail, setTechCheckEntryDetail] = useState<{
    date: string
    savedAt: string
    driverName?: string | null
    truck:   { item: string; status: string | null }[]
    trailer: { item: string; status: string | null }[]
  } | null>(null)

  // Valorizations state
  const [valorizationDialogOpen,  setValorizationDialogOpen]  = useState(false)
  const [editingValorization,     setEditingValorization]     = useState<ValorizationRow | null>(null)
  const [valorizationForm,        setValorizationForm]        = useState<ValorizationFormState>({ ...EMPTY_VALORIZATION_FORM })
  const [valorizationFormError,   setValorizationFormError]   = useState<string | null>(null)
  const [deletingValorizationId,  setDeletingValorizationId]  = useState<string | null>(null)
  const [valorizationDeleteOpen,  setValorizationDeleteOpen]  = useState(false)
  const [valorizationFile,        setValorizationFile]        = useState<File | null>(null)
  const [valorizationUploading,   setValorizationUploading]   = useState(false)
  const [internationalIdsConfirmOpen, setInternationalIdsConfirmOpen] = useState(false)
  const [pendingShipmentSave, setPendingShipmentSave] = useState<FormState | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Shipment docs upload
  const [docFile,      setDocFile]      = useState<File | null>(null)
  const [docNote,      setDocNote]      = useState("")
  const [docTypeId,    setDocTypeId]    = useState("")
  const [docLetter,    setDocLetter]    = useState("")
  const [docUploading, setDocUploading] = useState(false)
  const [docUploadErr, setDocUploadErr] = useState<string | null>(null)
  const docFileInputRef = useRef<HTMLInputElement>(null)

  async function handleDocUpload() {
    if (!docFile) return
    setDocUploading(true)
    setDocUploadErr(null)
    try {
      // 1. Presign
      const presignRes = await authorizedFetch(`${API_URL}/api/shipments/${id}/docs/presign`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ fileName: docFile.name, mimeType: docFile.type || "application/octet-stream" }),
      })
      if (!presignRes.ok) throw new Error("No se pudo obtener URL de subida")
      const { uploadUrl, key } = await presignRes.json() as { uploadUrl: string; key: string }

      // 2. Upload to S3
      const uploadRes = await fetch(uploadUrl, {
        method:  "PUT",
        headers: { "Content-Type": docFile.type || "application/octet-stream" },
        body:    docFile,
      })
      if (!uploadRes.ok) throw new Error("Error al subir el archivo")

      // 3. Create record
      const createRes = await authorizedFetch(`${API_URL}/api/shipments/${id}/docs`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          fileKey:  key,
          mimeType: docFile.type || "application/octet-stream",
          fileName: docFile.name,
          notes:    docNote.trim() || null,
          docTypeId: docTypeId || null,
          letter:   docLetter.trim().toUpperCase() || null,
        }),
      })
      if (!createRes.ok) throw new Error("Error al registrar el documento")

      setDocFile(null)
      setDocNote("")
      setDocTypeId("")
      setDocLetter("")
      if (docFileInputRef.current) docFileInputRef.current.value = ""
      void refetchDocs()
    } catch (err) {
      setDocUploadErr(err instanceof Error ? err.message : "Error al subir")
    } finally {
      setDocUploading(false)
    }
  }

  // ── Main query ────────────────────────────────────────────────────────────

  const { data: shipment, isLoading, isError } = useQuery({
    queryKey: ["shipment", id],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}`)
      if (!res.ok) throw new Error("Carga no encontrada")
      return res.json() as Promise<ShipmentDetail>
    },
    enabled: !!id,
  })

  // ── Select queries (lazy — only load when editing) ────────────────────────

  const {
    clients: clientOptions,
    onSearchChange: onClientSearchChange,
  } = useClientsSearch({ enabled: isEditing })

  const { data: partyOptions = [] } = useQuery({
    queryKey: ["parties-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/parties?deleted=false&page=1&pageSize=500`)
      if (!res.ok) return []
      const json = await res.json() as { data: PartyOption[] }
      return json.data
    },
    enabled:   isEditing,
    staleTime: 60_000,
  })

  const { data: routeOptions = [] } = useQuery<RouteOption[]>({
    queryKey: ["geo", "routes", "shipment-detail-select"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/geo/routes`)
      if (!res.ok) return []
      return (await res.json() as RouteOption[]).filter(route => !route.deletedAt)
    },
    enabled: isEditing,
    staleTime: 60_000,
  })

  const { data: nationalRouteStopRows = [] } = useQuery({
    queryKey: ["shipment-route-stops", id, "detail"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}/stops`)
      if (!res.ok) return [] as Array<{ partyId: string; kind: NationalRouteStop["kind"]; partyName: string; cityName: string | null }>
      return res.json() as Promise<Array<{ partyId: string; kind: NationalRouteStop["kind"]; partyName: string; cityName: string | null }>>
    },
    enabled: !!id && shipment?.workLine === "nacional",
    staleTime: 60_000,
  })
  const nationalRouteStops: NationalRouteStop[] = nationalRouteStopRows.map(stop => ({
    partyId: stop.partyId,
    kind: stop.kind,
  }))

  const { data: cargoTypeOptions = [] } = useQuery({
    queryKey: ["cargo-types-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/cargos/types?deleted=false`)
      return res.ok ? (res.json() as Promise<CargoTypeOption[]>) : []
    },
    enabled:   isEditing,
    staleTime: 60_000,
  })

  // ── Trip queries (always loaded for the section) ───────────────────────────

  const { data: tripsData = [], isLoading: tripsLoading } = useQuery({
    queryKey: ["trips", "shipment", id],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/trips?shipmentId=${id}&deleted=false&pageSize=500`)
      if (!res.ok) return []
      const json = await res.json() as { data: TripRow[] }
      return json.data
    },
    enabled: !!id,
  })

  const { data: mobileOptions = [] } = useQuery({
    queryKey: ["mobiles-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/mobiles`)
      return res.ok ? (res.json() as Promise<MobileOption[]>) : []
    },
    enabled:   tripSheetOpen || splitOpen,
    staleTime: 60_000,
  })

  const { data: cityOptions = [] } = useQuery({
    queryKey: ["cities-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/geo/cities?deleted=false&type=city`)
      return res.ok ? (res.json() as Promise<CityOption[]>) : []
    },
    enabled:   tripSheetOpen || splitOpen,
    staleTime: 60_000,
  })

  const { data: driverList = [] } = useQuery({
    queryKey: ["drivers-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/providers/drivers?deleted=false&pageSize=500`)
      if (!res.ok) return []
      const json = await res.json() as { data: { id: string; name: string; code: string }[] }
      return json.data
    },
    enabled:   tripSheetOpen || splitOpen,
    staleTime: 60_000,
  })

  const { data: unitList = [] } = useQuery({
    queryKey: ["units-select", unitSearchDebounced],
    queryFn:  async () => {
      const params = new URLSearchParams({ deleted: "false" })
      if (unitSearchDebounced.trim()) params.set("search", unitSearchDebounced.trim())
      const res = await authorizedFetch(`${API_URL}/api/units?${params}`)
      if (!res.ok) return []
      const body = await res.json() as { data: { id: string; licensePlate: string | null; unitNumber: number | null }[]; total: number }
      return body.data ?? []
    },
    enabled:   tripSheetOpen || splitOpen,
    staleTime: 60_000,
  })

  type UnitOption = {
    id:              string
    licensePlate:    string | null
    unitNumber:      number | null
    brand:           string | null
    model:           string | null
    operatorName:    string | null
    inMobile:        boolean
    manufactureYear: number | null
    unitTypeCode:    string | null
  }

  const { data: splitTruckList = [] } = useQuery({
    queryKey: ["split-units-truck", splitTruckSearchDb],
    queryFn:  async () => {
      const params = new URLSearchParams({ deleted: "false", unitTypeCodes: "C,TRACTORGNC" })
      if (splitTruckSearchDb.trim()) params.set("search", splitTruckSearchDb.trim())
      const res = await authorizedFetch(`${API_URL}/api/units?${params}`)
      if (!res.ok) return [] as UnitOption[]
      const body = await res.json() as { data: UnitOption[]; total: number }
      return body.data ?? []
    },
    enabled:   splitOpen && splitTruckSearchDb.trim().length >= 2,
    staleTime: 30_000,
  })

  const { data: splitTrailerList = [] } = useQuery({
    queryKey: ["split-units-trailer", splitTrailerSearchDb],
    queryFn:  async () => {
      const params = new URLSearchParams({ deleted: "false", unitTypeCodes: "S,BATEA,BIVUELCO,PLAYO" })
      if (splitTrailerSearchDb.trim()) params.set("search", splitTrailerSearchDb.trim())
      const res = await authorizedFetch(`${API_URL}/api/units?${params}`)
      if (!res.ok) return [] as UnitOption[]
      const body = await res.json() as { data: UnitOption[]; total: number }
      return body.data ?? []
    },
    enabled:   splitOpen && splitTrailerSearchDb.trim().length >= 2,
    staleTime: 30_000,
  })

  const { data: valorizations = [], isLoading: valorizationsLoading } = useQuery({
    queryKey: ["shipment-valorizations", id],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}/valorizations`)
      return res.ok ? (res.json() as Promise<ValorizationRow[]>) : []
    },
    enabled: !!id && isAdminOrOperator,
  })

  const { data: finalDestinationOptions = [] } = useQuery<{ id: string; name: string; code: string }[]>({
    queryKey: ["final-destinations-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/batea/final-destinations?deleted=false`)
      return res.ok ? (res.json() as Promise<{ id: string; name: string; code: string }[]>) : []
    },
    staleTime: 60_000,
  })

  const { data: shipmentDocTypes = [] } = useQuery({
    queryKey: ["shipment-doc-types", "active"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipment-doc-types?deleted=false`)
      return res.ok ? (res.json() as Promise<ShipmentDocTypeOption[]>) : []
    },
    enabled: isAdminOrOperator,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!docTypeId) {
      const defaultType = shipmentDocTypes.find(type => type.code === DEFAULT_SHIPMENT_DOC_TYPE_CODE)
      if (defaultType) {
        setDocTypeId(defaultType.id)
        setDocLetter(defaultType.control ?? "")
      }
    }
  }, [docTypeId, shipmentDocTypes])

  const { data: shipmentDocs = [], refetch: refetchDocs } = useQuery({
    queryKey: ["shipment-docs", id],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}/docs`)
      return res.ok ? (res.json() as Promise<ShipmentDocRow[]>) : []
    },
    enabled: !!id,
  })

  // ── Mutations ─────────────────────────────────────────────────────────────

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["shipment", id] })
    qc.invalidateQueries({ queryKey: ["shipments"] })
  }

  function invalidateTrips() {
    qc.invalidateQueries({ queryKey: ["trips", "shipment", id] })
    qc.invalidateQueries({ queryKey: ["trips"] })
  }

  function invalidateValorizations() {
    qc.invalidateQueries({ queryKey: ["shipment-valorizations", id] })
  }

  function openCreateValorization() {
    setEditingValorization(null)
    setValorizationForm({ ...EMPTY_VALORIZATION_FORM })
    setValorizationFormError(null)
    setValorizationFile(null)
    setValorizationDialogOpen(true)
  }

  function openEditValorization(v: ValorizationRow) {
    setEditingValorization(v)
    setValorizationForm({ type: v.type, quantity: v.quantity, value: v.value, notes: v.notes ?? "" })
    setValorizationFormError(null)
    setValorizationFile(null)
    setValorizationDialogOpen(true)
  }

  const valorizationMut = useMutation({
    mutationFn: async (form: ValorizationFormState) => {
      let fileKey: string | null = null
      let mimeType: string | null = null

      // Upload file first if selected
      if (valorizationFile) {
        setValorizationUploading(true)
        const presignRes = await authorizedFetch(`${API_URL}/api/shipments/${id}/valorizations/presign`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ fileName: valorizationFile.name, mimeType: valorizationFile.type }),
        })
        if (!presignRes.ok) throw new Error("No se pudo obtener URL de subida")
        const { uploadUrl, key } = await presignRes.json() as { uploadUrl: string; key: string }

        const uploadRes = await fetch(uploadUrl, {
          method:  "PUT",
          headers: { "Content-Type": valorizationFile.type },
          body:    valorizationFile,
        })
        if (!uploadRes.ok) throw new Error("Error al subir el archivo")
        fileKey  = key
        mimeType = valorizationFile.type
        setValorizationUploading(false)
      }

      const body: Record<string, unknown> = {
        type:     form.type,
        quantity: parseFloat(form.quantity),
        value:    parseFloat(form.value),
        notes:    form.notes || null,
      }
      if (fileKey) {
        body.fileKey  = fileKey
        body.mimeType = mimeType
      }

      const url    = editingValorization
        ? `${API_URL}/api/shipments/${id}/valorizations/${editingValorization.id}`
        : `${API_URL}/api/shipments/${id}/valorizations`
      const method = editingValorization ? "PATCH" : "POST"

      const res = await authorizedFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(body),
      })
      if (!res.ok) {
        const err = await res.json() as { error?: string }
        throw new Error(err.error ?? "Error al guardar valorización")
      }
    },
    onSuccess: () => {
      invalidateValorizations()
      setValorizationDialogOpen(false)
      setValorizationUploading(false)
      toast.success(editingValorization ? "Valorización actualizada" : "Valorización creada")
    },
    onError: (err: Error) => {
      setValorizationUploading(false)
      setValorizationFormError(err.message)
    },
  })

  const deleteValorizationMut = useMutation({
    mutationFn: async (valId: string) => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}/valorizations/${valId}`, {
        method: "DELETE",
      })
      if (!res.ok) {
        const err = await res.json() as { error?: string }
        throw new Error(err.error ?? "Error al eliminar")
      }
    },
    onSuccess: () => {
      invalidateValorizations()
      setValorizationDeleteOpen(false)
      setDeletingValorizationId(null)
      toast.success("Valorización eliminada")
    },
    onError: (err: Error) => setValorizationFormError(err.message),
  })

  // ── Milestone mutation (single-field PATCH) ─────────────────────────────────

  const [milestoneEdit, setMilestoneEdit] = useState<{
    key:   keyof ShipmentDetail
    label: string
    value: string
  } | null>(null)
  const [milestoneError, setMilestoneError] = useState<string | null>(null)

  const milestoneMut = useMutation({
    mutationFn: async ({ key, value }: { key: keyof ShipmentDetail; value: string | null }) => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ [key]: value ? new Date(value).toISOString() : null }),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al guardar hito")
      }
    },
    onSuccess: () => { invalidate(); setMilestoneEdit(null); setMilestoneError(null) },
    onError:   (err: Error) => setMilestoneError(err.message),
  })

  // Reverts a finalized batea shipment back to en_curso (clears finalizedAt, reverts last trip)
  const revertFinalizationMut = useMutation({
    mutationFn: async () => {
      // 1. Revert shipment status + clear finalizedAt
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ status: "en_curso", finalizedAt: null }),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al revertir el envío")
      }
      // 2. Revert the last completado trip back to en_curso (most recently created)
      const lastTrip = [...tripsData]
        .filter(t => t.status === "completado")
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
      if (lastTrip) {
        await authorizedFetch(`${API_URL}/api/trips/${lastTrip.id}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ status: "en_curso" }),
        })
      }
    },
    onSuccess: () => { invalidate(); invalidateTrips() },
    onError:   (err: Error) => console.error("revertFinalization", err.message),
  })

  const deleteTripPlanMut = useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ tripPlan: null }),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar la planificación de viaje")
      }
    },
    onSuccess: () => {
      invalidate()
      setDeleteTripPlanOpen(false)
      toast.success("Planificación de viaje eliminada")
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const deleteTechCheckMut = useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ techCheck: null }),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar la verificación técnica")
      }
    },
    onSuccess: () => {
      invalidate()
      setDeleteTechCheckOpen(false)
      toast.success("Verificación técnica eliminada")
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const updateMut = useMutation({
    mutationFn: async (f: FormState) => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(buildPayload(f, true)),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar el envío")
      }
    },
    onSuccess: () => {
      invalidate()
      toast.success("Carga actualizada")
      setIsEditing(false)
      setFormError(null)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}`, { method: "DELETE" })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar el envío")
      }
    },
    onSuccess: () => {
      toast.success("Carga eliminada")
      navigate("/shipments")
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}/restore`, { method: "PATCH" })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al restaurar el envío")
      }
    },
    onSuccess: () => {
      invalidate()
      toast.success("Carga restaurada")
    },
    onError: (err: Error) => toast.error(err.message),
  })

  // ── Trip mutations ─────────────────────────────────────────────────────────

  const createTripMut = useMutation({
    mutationFn: async (f: TripFormState) => {
      // Look up the mobile's snapshot resources so the trip displays correctly
      const mobile = mobileOptions.find(m => m.id === f.mobileId)
      const res = await authorizedFetch(`${API_URL}/api/trips`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shipmentId:        id,
          mobileId:          f.mobileId          || null,
          driverId:          mobile?.driverId     ?? null,
          truckId:           mobile?.truckId      ?? null,
          trailerId:         mobile?.trailerId    ?? null,
          originCityId:      f.originCityId      || null,
          destinationCityId: f.destinationCityId || null,
          departureAt:       f.departureAt ? new Date(f.departureAt).toISOString() : null,
          arrivalAt:         f.arrivalAt   ? new Date(f.arrivalAt).toISOString()   : null,
          status:            f.status,
          notes:             f.notes || null,
          hasTravelAdvance:  f.hasTravelAdvance,
        }),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al crear el viaje")
      }
      return res.json()
    },
    onSuccess: () => { invalidateTrips(); toast.success("Viaje agregado"); closeTripSheet() },
    onError:   (err: Error) => setTripFormError(err.message),
  })

  const updateTripMut = useMutation({
    mutationFn: async (f: TripFormState) => {
      if (!editingTrip) throw new Error("Sin viaje seleccionado")
      const res = await authorizedFetch(`${API_URL}/api/trips/${editingTrip.id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          driverId:          f.driverId          || null,
          truckId:           f.truckId           || null,
          trailerId:         f.trailerId         || null,
          originCityId:      f.originCityId      || null,
          destinationCityId: f.destinationCityId || null,
          departureAt:       f.departureAt ? new Date(f.departureAt).toISOString() : null,
          arrivalAt:         f.arrivalAt   ? new Date(f.arrivalAt).toISOString()   : null,
          status:            f.status,
          notes:             f.notes || null,
          hasTravelAdvance:  f.hasTravelAdvance,
        }),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar el viaje")
      }
      return res.json()
    },
    onSuccess: () => { invalidateTrips(); toast.success("Viaje actualizado"); closeTripSheet() },
    onError:   (err: Error) => setTripFormError(err.message),
  })

  const deleteTripMut = useMutation({
    mutationFn: async (tripId: string) => {
      const res = await authorizedFetch(`${API_URL}/api/trips/${tripId}`, { method: "DELETE" })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar el viaje")
      }
    },
    onSuccess: () => {
      invalidateTrips()
      toast.success("Viaje eliminado")
      setTripDeleteOpen(false)
      setDeletingTrip(null)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const splitTripMut = useMutation({
    mutationFn: async ({
      trip, mobileId, cityId, driverId, truckId, trailerId,
    }: {
      trip: TripRow; mobileId: string; cityId: string
      driverId: string; truckId: string; trailerId: string
    }) => {
      // Use undefined for empty strings — JSON.stringify omits them, preventing
      // the API from accidentally nulling out mobile resources that were not changed
      const res = await authorizedFetch(`${API_URL}/api/trips/${trip.id}/split`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          cityId:    cityId,
          mobileId:  mobileId  || undefined,
          driverId:  driverId  || undefined,
          truckId:   truckId   || undefined,
          trailerId: trailerId || undefined,
        }),
      })
      if (!res.ok) {
        const b = await res.json() as { error?: string }
        throw new Error(b.error ?? "Error al dividir el viaje")
      }
    },
    onSuccess: () => {
      invalidateTrips()
      qc.invalidateQueries({ queryKey: ["mobiles-select"] })
      toast.success("Viaje dividido en dos tramos")
      setSplitOpen(false)
      setSplitingTrip(null)
      setSplitMobileId("")
      setSplitCityId("")
      setSplitDriverId("")
      setSplitTruckId("")
      setSplitTrailerId("")
      setSplitTruckSearch(""); setSplitTruckSearchDb("")
      setSplitTrailerSearch(""); setSplitTrailerSearchDb("")
    },
    onError: (err: Error) => toast.error(err.message),
  })

  // ── Helpers ─────────────────────────────────────────────────────────────────────

  const mobileEditor = useMobileEditor({
    mobileId:      tripForm.mobileId,
    mobileOptions,
    driverList,
    unitList,
    onError:      (msg) => setTripFormError(msg),
    onUnitSearch: setUnitSearch,
  })

  // Split dialog: auto-populate resources when a mobile is selected
  function onSplitMobileSelected(newId: string) {
    setSplitMobileId(newId)
    const m = mobileOptions.find(m => m.id === newId)
    setSplitDriverId(m?.driverId  ?? "")
    setSplitTruckId(m?.truckId   ?? "")
    setSplitTrailerId(m?.trailerId ?? "")
  }

  // Build options for the split dialog selects — all mobile options (including same mobile)
  const splitMobileSelectOptions = useMemo<SearchableOption[]>(() => {
    function inactiveReason(m: MobileOptionBase): string {
      const missing: string[] = []
      if (!m.driverId)  missing.push("sin chofer")
      if (!m.trailerId) missing.push("sin semi")
      return missing.length > 0 ? missing.join(", ") : "inactivo"
    }
    return [
      { value: "__none__", label: "Sin asignar" },
      ...mobileOptions.map(m => ({
        value:     m.id,
        label:     m.truckUnitNumber != null ? `M${m.truckUnitNumber}` : (m.truckLicensePlate ?? m.id),
        sublabel:  m.isActive ? (m.driverName ?? undefined) : inactiveReason(m),
        keywords:  [m.operatorName, m.driverName].filter(Boolean).join(" "),
        disabled:  !m.isActive,
        startIcon: m.operatorName ? (
          <Avatar className="size-5">
            <AvatarFallback style={{ backgroundColor: avatarColor(m.operatorName), color: "white" }} className="text-[9px] font-semibold">
              {avatarInitials(m.operatorName)}
            </AvatarFallback>
          </Avatar>
        ) : undefined,
      })),
    ]
  }, [mobileOptions])

  const splitDriverSelectOptions = useMemo<SearchableOption[]>(() => [
    { value: "__none__", label: "Sin chofer" },
    ...driverList.map(d => ({ value: d.id, label: d.name, sublabel: d.code })),
  ], [driverList])

  function buildUnitOption(u: UnitOption): SearchableOption {
    // Line 1: brand + model (descriptive name), fallback to license plate
    const nameParts = [u.brand, u.model].filter(Boolean)
    const label = nameParts.length > 0 ? nameParts.join(" ") : (u.licensePlate ?? `Unidad ${u.unitNumber ?? "—"}`)
    // Line 2: plate  type  year
    const meta: string[] = []
    if (u.licensePlate)      meta.push(u.licensePlate)
    if (u.unitTypeCode)      meta.push(u.unitTypeCode)
    if (u.manufactureYear)   meta.push(String(u.manufactureYear))
    return {
      value:    u.id,
      label,
      sublabel: meta.join("   ") || undefined,
    }
  }

  const splitTruckSelectOptions = useMemo<SearchableOption[]>(() => [
    { value: "__none__", label: "Sin camión" },
    ...splitTruckList.map(buildUnitOption),
  ], [splitTruckList])

  const splitTrailerSelectOptions = useMemo<SearchableOption[]>(() => [
    { value: "__none__", label: "Sin semi" },
    ...splitTrailerList.map(buildUnitOption),
  ], [splitTrailerList])

  function closeTripSheet() {
    setTripSheetOpen(false)
    setEditingTrip(null)
    setTripForm({ ...EMPTY_TRIP_FORM })
    setTripFormError(null)
    setEditResources(false)
    mobileEditor.reset()
    setUnitSearch("")
    setUnitSearchDebounced("")
  }

  function openNewTrip() {
    setEditingTrip(null)
    setTripForm({ ...EMPTY_TRIP_FORM })
    setTripFormError(null)
    mobileEditor.reset()
    setTripSheetOpen(true)
  }

  function openEditTrip(t: TripRow) {
    setEditingTrip(t)
    setTripForm({
      mobileId:          t.mobileId          ?? "",
      driverId:          t.driverId          ?? "",
      truckId:           t.truckId           ?? "",
      trailerId:         t.trailerId         ?? "",
      originCityId:      t.originCityId      ?? "",
      destinationCityId: t.destinationCityId ?? "",
      departureAt:       isoToLocalDatetime(t.departureAt),
      arrivalAt:         isoToLocalDatetime(t.arrivalAt),
      status:            t.status,
      notes:             t.notes ?? "",
      hasTravelAdvance:  normalizeTravelAdvance(t.hasTravelAdvance),
    })
    setTripFormError(null)
    mobileEditor.reset()
    setTripSheetOpen(true)
  }

  function doTripSave(f: TripFormState) {
    if (editingTrip) updateTripMut.mutate(f)
    else             createTripMut.mutate(f)
  }

  function handleTripSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTripFormError(null)
    if (mobileEditor.wasEdited) {
      mobileEditor.openConfirm()
    } else {
      doTripSave(tripForm)
    }
  }

  const citySelectOptions = [
    { value: "__none__", label: "Sin ciudad" },
    ...cityOptions.map(c => ({ value: c.id, label: c.name, sublabel: c.code })),
  ]

  function openEdit() {
    if (!shipment) return
    setForm({
      status:                        shipment.status,
      clientId:                      shipment.clientId    ?? "",
      cargoTypeId:                   shipment.cargoTypeId ?? "",
      externalId:                    shipment.externalId  ?? "",
      cargoValue:                    shipment.cargoValue  ?? "",
      workLine:                      (shipment.workLine   ?? "") as WorkLine | "",
      senderId:                      shipment.senderId    ?? "",
      recipientId:                   shipment.recipientId ?? "",
      scheduledOriginArrivalAt:      isoToLocalDatetime(shipment.scheduledOriginArrivalAt),
      scheduledDestinationArrivalAt: isoToLocalDatetime(shipment.scheduledDestinationArrivalAt),
      notes:                         shipment.notes        ?? "",
      billingNotes:                  shipment.billingNotes ?? "",
      billingStatus:                 shipment.billingStatus ?? null,
      deliveryNoteNumber:    shipment.deliveryNoteNumber    ?? "",
      loadingWeightKg:       shipment.loadingWeightKg       ?? "",
      unloadingWeightKg:     shipment.unloadingWeightKg     ?? "",
      deliveryNoteStatus:    shipment.deliveryNoteStatus,
      loadingWindow:       shipment.loadingWindow      ?? "",
      finalDestinationId: shipment.finalDestinationId ?? "",
      routeId:            shipment.routeId        ?? "",
      routeStops:         [],
      isRouted:           shipment.isRouted,
      isMultiLeg:         shipment.isMultiLeg,
      billingTags:        shipment.billingTags.map(t => t.label),
      internationalIds:   shipment.internationalIds ?? [],
      confirmInternationalIdsDeletion: false,
      products: shipment.products.map(p => ({
        productId: p.productId,
        quantity:  p.quantity  ?? "",
        weightKg:  p.weightKg  ?? "",
        notes:     p.notes     ?? "",
      })),
    })
    setFormError(null)
    setIsEditing(true)
  }

  useEffect(() => {
    if (!isEditing || form.workLine !== "nacional" || form.routeStops.length > 0 || nationalRouteStops.length === 0) return
    setForm(previous => ({ ...previous, routeStops: nationalRouteStops }))
  }, [isEditing, form.workLine, form.routeStops.length, nationalRouteStops])

  function handleInlineCancel() {
    setIsEditing(false)
    setFormError(null)
  }

  function setRouteEndpoint(field: "senderId" | "recipientId", value: string) {
    setForm(previous => ({
      ...previous,
      [field]: value,
      ...(previous.workLine === "nacional"
        ? {
            routeStops: syncNationalRouteStops(
              field === "senderId" ? value : previous.senderId,
              field === "recipientId" ? value : previous.recipientId,
              previous.routeStops,
            ),
          }
        : {}),
    }))
  }

  function requestShipmentSave(nextForm: FormState) {
    const payloadInternationalIds = buildPayload(nextForm, true).internationalIds
    const deletingIds = shipment?.internationalIds?.some(value => !payloadInternationalIds.includes(value))
    if (deletingIds) {
      setPendingShipmentSave({ ...nextForm, confirmInternationalIdsDeletion: true })
      setInternationalIdsConfirmOpen(true)
      return
    }
    updateMut.mutate(nextForm)
  }

  function handleInlineSave() {
    setFormError(null)
    if (!form.senderId && !form.clientId) {
      setFormError("Debe indicar al menos un remitente o cliente")
      return
    }
    requestShipmentSave(form)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!form.senderId && !form.clientId) {
      setFormError("Debe indicar al menos un remitente o cliente")
      return
    }
    requestShipmentSave(form)
  }

  // ── Select option helpers ─────────────────────────────────────────────────

  const clientSelectOptions = [
    { value: "__none__", label: "Sin asignar" },
    ...clientOptions.map(c => ({ value: c.id, label: c.name, sublabel: c.code })),
  ]
  const cargoTypeSelectOptions = [
    { value: "__none__", label: "Sin asignar" },
    ...cargoTypeOptions.map(t => ({ value: t.id, label: t.name, sublabel: t.code })),
  ]
  const partySelectOptions = [
    { value: "__none__", label: "Sin asignar" },
    ...partyOptions.map(p => ({
      value:    p.id,
      label:    p.name,
      sublabel: [p.externalId ? `ID: ${p.externalId}` : null, p.cityName, p.countryName].filter(Boolean).join(" / ") || undefined,
      keywords: p.externalId ?? undefined,
    })),
  ]

  const selectedRoute = form.workLine === "nacional"
    ? routeOptions.find(route => route.id === form.routeId) ?? null
    : null
  const senderParty = partyOptions.find(party => party.id === form.senderId) ?? null
  const recipientParty = partyOptions.find(party => party.id === form.recipientId) ?? null
  const connectionRoute = senderParty?.cityId && recipientParty?.cityId
    ? routeOptions.find(route => route.originCityId === senderParty.cityId && route.targetCityId === recipientParty.cityId) ?? null
    : null
  const effectiveRoute = selectedRoute ?? connectionRoute
  const cargoRoute = selectedRoute && senderParty?.cityId
    ? routeOptions.find(route => route.originCityId === senderParty.cityId && route.targetCityId === selectedRoute.targetCityId) ?? null
    : effectiveRoute

  // ── Journey milestones filtered by workLine ───────────────────────────────

  const visibleMilestones = shipment
    ? JOURNEY_MILESTONES.filter(m =>
        !m.onlyFor || (shipment.workLine && m.onlyFor.includes(shipment.workLine))
      )
    : []

  const hasAnyJourneyTimestamp = shipment
    ? JOURNEY_MILESTONES.some(m => !!shipment[m.key])
    : false

  // ── Loading / error states ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <>
        <SiteHeader title="Carga" />
        <div className="flex flex-1 items-center justify-center text-muted-foreground py-24">
          Cargando...
        </div>
      </>
    )
  }

  if (isError || !shipment) {
    return (
      <>
        <SiteHeader title="Carga no encontrada" />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
          <p className="text-muted-foreground">No se encontró la carga.</p>
          <Button variant="outline" onClick={() => navigate("/shipments")}>
            <ArrowLeftIcon className="size-4 mr-2" />
            Volver a Cargas
          </Button>
        </div>
      </>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title={`Carga #${shipment.internalId}`} />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6 space-y-6">

        {/* Back link */}
        <Link
          to="/shipments"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          <ArrowLeftIcon className="size-3.5" />
          Volver a Cargas
        </Link>

        {/* ── Page header ── */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              Carga{" "}
              <span className="font-mono text-muted-foreground">#{shipment.internalId}</span>
              {shipment.internationalIds?.length > 0 && (
                <span className="ml-2 font-mono text-sm text-muted-foreground/70 font-normal">
                  · {shipment.internationalIds.join(", ")}
                </span>
              )}
              {shipment.externalId && (
                <span className="ml-2 font-mono text-sm text-muted-foreground/60 font-normal">
                  · {shipment.externalId}
                </span>
              )}
            </h1>
            <span className={cn(
              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
              STATUS_COLORS[shipment.status],
            )}>
              {STATUS_LABELS[shipment.status]}
            </span>
            {shipment.workLine && (
              <span className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                WORK_LINE_COLORS[shipment.workLine],
              )}>
                {WORK_LINE_LABELS[shipment.workLine]}
              </span>
            )}
            {shipment.isRouted && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-2.5 py-0.5 text-xs font-medium">
                <RouteIcon className="size-3" />
                Ruteado
              </span>
            )}
            {shipment.deletedAt && (
              <span className="inline-flex items-center rounded-full bg-destructive/10 text-destructive px-2.5 py-0.5 text-xs font-medium">
                Eliminado
              </span>
            )}
          </div>

          {can("write", "shipments") && (
            <div className="flex items-center gap-2">
              {shipment.deletedAt ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => restoreMut.mutate()}
                  disabled={restoreMut.isPending}
                  className="text-emerald-600 hover:text-emerald-700"
                >
                  <RestoreIcon className="size-4 mr-2" />
                  {restoreMut.isPending ? "Restaurando..." : "Restaurar"}
                </Button>
              ) : isEditing ? (
                <>
                  {formError && (
                    <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-1.5">
                      {formError}
                    </p>
                  )}
                  <Button variant="outline" size="sm" onClick={handleInlineCancel}>
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={handleInlineSave} disabled={updateMut.isPending}>
                    {updateMut.isPending ? "Guardando..." : "Guardar cambios"}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={openEdit}>
                    <PencilIcon className="size-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteOpen(true)}
                    className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
                  >
                    <Trash2Icon className="size-4 mr-2" />
                    Eliminar
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Cancel banner ── */}
        {shipment.status === "cancelado" && shipment.cancelReason && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-900/10 px-4 py-3">
            <BanIcon className="size-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-red-700 dark:text-red-400">
                Cancelado · {CANCEL_REASON_LABELS[shipment.cancelReason] ?? shipment.cancelReason}
              </span>
              {shipment.cancelDescription && (
                <span className="text-sm text-red-600/80 dark:text-red-400/70">{shipment.cancelDescription}</span>
              )}
            </div>
          </div>
        )}

        {/* ── Two-column layout for Info + Ruta ── */}
        <div className="grid gap-5 lg:grid-cols-2">

          {/* Section: Info general */}
          <SectionCard title="Información general" icon={<FileTextIcon className="size-4" />}>
            {isEditing ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div className="grid gap-1.5">
                  <Label>Estado <span className="text-destructive">*</span></Label>
                  <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v as ShipmentStatus }))}>
                    <SelectTrigger><SelectValue>{STATUS_LABELS[form.status] ?? form.status}</SelectValue></SelectTrigger>
                    <SelectContent>{EDITABLE_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Línea de trabajo</Label>
                  <Select value={form.workLine || "__none__"} onValueChange={(v) => setForm(f => ({ ...f, workLine: v === "__none__" ? "" : v as WorkLine }))}>
                    <SelectTrigger><SelectValue>{form.workLine ? WORK_LINE_LABELS[form.workLine as WorkLine] : <span className="text-muted-foreground">Sin asignar</span>}</SelectValue></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin asignar</SelectItem>
                      <SelectItem value="nacional">Nacional</SelectItem>
                      <SelectItem value="internacional">Internacional</SelectItem>
                      <SelectItem value="batea">Batea</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.workLine === "internacional" && (
                  <InternationalShipmentIdsEditor
                    className="col-span-2"
                    values={form.internationalIds}
                    corridor={nextInternationalIdQuery.data?.corridor ?? null}
                    description="Corredor determinado por origen y destino. Los IDs deben respetar el prefijo configurado."
                    suggestedNext={(values) => nextInternationalIdQuery.data?.seed
                      ? nextInternationalId(nextInternationalIdQuery.data.seed, values)
                      : null}
                    nextValue={nextInternationalIdQuery.data?.next ?? null}
                    onChange={(internationalIds) => setForm(f => ({ ...f, internationalIds }))}
                  />
                )}
                {form.workLine === "internacional" && (
                  <div className="grid gap-1.5 col-span-2">
                    <Label htmlFor="dp-cargo-value">Valor de la mercadería (ARS)</Label>
                    <Input
                      id="dp-cargo-value"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.cargoValue}
                      onChange={(e) => setForm(f => ({ ...f, cargoValue: e.target.value }))}
                      placeholder="Ingrese el valor en ARS..."
                    />
                  </div>
                )}
                <div className="grid gap-1.5">
                  <Label>Cliente</Label>
                  <SearchableSelect options={clientSelectOptions} value={form.clientId || "__none__"} onValueChange={(v) => setForm(f => ({ ...f, clientId: v === "__none__" ? "" : v }))} onSearchChange={onClientSearchChange} placeholder="Sin asignar" searchPlaceholder="Buscar cliente..." />
                </div>
                <div className="grid gap-1.5 col-span-2">
                  <Label>N° Externo</Label>
                  <Input value={form.externalId} onChange={(e) => setForm(f => ({ ...f, externalId: e.target.value }))} maxLength={100} placeholder="Identificador del cliente..." />
                </div>
                <div className="grid gap-1.5 col-span-2">
                  <Label>Observaciones</Label>
                  <textarea rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notas u observaciones..." className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none" />
                </div>
                <div className="grid gap-1.5 col-span-2">
                  <Label>Observaciones de facturación</Label>
                  <textarea rows={3} value={form.billingNotes} onChange={(e) => setForm(f => ({ ...f, billingNotes: e.target.value }))} placeholder="Notas para el área de facturación..." className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none" />
                </div>
                <div className="grid gap-1.5 col-span-2">
                  <Label>Etiquetas de facturación</Label>
                  <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
                    {form.billingTags.map((tag, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2.5 py-0.5 text-xs font-medium"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, billingTags: f.billingTags.filter((_, j) => j !== i) }))}
                          className="hover:text-violet-900 dark:hover:text-violet-100 transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <Input
                    placeholder="Agregar etiqueta y presionar Enter o coma..."
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault()
                        const val = (e.target as HTMLInputElement).value.trim()
                        if (val && !form.billingTags.includes(val)) {
                          setForm(f => ({ ...f, billingTags: [...f.billingTags, val] }))
                        }
                        ;(e.target as HTMLInputElement).value = ""
                      }
                    }}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border px-3 py-2.5 col-span-2">
                  <div className="space-y-0.5">
                    <Label htmlFor="dp-is-routed" className="flex items-center gap-1.5 cursor-pointer">
                      <RouteIcon className="size-3.5 text-muted-foreground" />
                      Ruteado
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      El cliente confirmó el camión y chofer asignados
                    </p>
                  </div>
                  <input
                    id="dp-is-routed"
                    type="checkbox"
                    checked={form.isRouted}
                    onChange={(e) => setForm(f => ({ ...f, isRouted: e.target.checked }))}
                    className="size-4 rounded border-border cursor-pointer accent-primary"
                  />
                </div>
              </div>
            ) : (
              <>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <Field label="Cliente" value={shipment.clientName} />
                  {shipment.products[0] && (
                    <Field label="Producto" value={shipment.products[0].productName} />
                  )}
                  <Field label="Línea de trabajo" value={shipment.workLine ? WORK_LINE_LABELS[shipment.workLine] : null} />
                  {shipment.workLine === "internacional" && (
                    <Field label="Valor de la mercadería (ARS)" value={fmtCargoValue(shipment.cargoValue)} />
                  )}
                  <Field label="Creado"      value={fmtDateLong(shipment.createdAt)} />
                  <Field label="Actualizado" value={fmtDateLong(shipment.updatedAt)} />
                </dl>
                {shipment.billingTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {shipment.billingTags.map(t => (
                      <span key={t.id} className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2.5 py-0.5 text-xs font-medium">
                        {t.label}
                      </span>
                    ))}
                  </div>
                )}
                {(shipment.notes || shipment.billingNotes) && (
                  <div className="flex flex-col gap-3 pt-2 border-t">
                    {shipment.notes && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-muted-foreground">Observaciones</span>
                        <p className="text-sm whitespace-pre-wrap">{shipment.notes}</p>
                      </div>
                    )}
                    {shipment.billingNotes && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-muted-foreground">Observaciones de facturación</span>
                        <p className="text-sm whitespace-pre-wrap">{shipment.billingNotes}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </SectionCard>

          {/* Section: Ruta */}
          <SectionCard title="Ruta" icon={<MapPinIcon className="size-4" />}>
            {isEditing ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div className="grid gap-1.5">
                  <Label>Remitente</Label>
                  <SearchableSelect options={partySelectOptions} value={form.senderId || "__none__"} onValueChange={(v) => setRouteEndpoint("senderId", v === "__none__" ? "" : v)} placeholder="Sin asignar" searchPlaceholder="Buscar remitente..." />
                </div>
                <div className="grid gap-1.5">
                  <Label>Destinatario</Label>
                  <SearchableSelect options={partySelectOptions} value={form.recipientId || "__none__"} onValueChange={(v) => setRouteEndpoint("recipientId", v === "__none__" ? "" : v)} placeholder="Sin asignar" searchPlaceholder="Buscar destinatario..." />
                </div>
                {form.workLine === "nacional" && (
                  <div className="grid gap-3 col-span-2">
                    <NationalRouteEditor
                      senderId={form.senderId}
                      recipientId={form.recipientId}
                      routeId={form.routeId}
                      routeStops={form.routeStops}
                      routeOptions={routeOptions}
                      partyOptions={partyOptions}
                      onRouteIdChange={routeId => setForm(previous => ({ ...previous, routeId }))}
                      onRouteStopsChange={routeStops => setForm(previous => ({ ...previous, routeStops }))}
                    />
                    {form.senderId && form.recipientId && (
                      <div className="grid gap-2">
                        <ShipmentRouteInfoBlock
                          title="Datos de viaje"
                          origin={effectiveRoute?.originCityName ?? senderParty?.cityName}
                          destination={effectiveRoute?.targetCityName ?? recipientParty?.cityName}
                          km={effectiveRoute?.km}
                          code={effectiveRoute?.code}
                        />
                        <ShipmentRouteInfoBlock
                          title="Datos de carga"
                          origin={senderParty?.cityName}
                          destination={effectiveRoute?.targetCityName ?? recipientParty?.cityName}
                          km={cargoRoute?.km ?? shipment.shipmentKm}
                          code={cargoRoute?.code ?? shipment.cargoRouteCode}
                          showKm={false}
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className="grid gap-1.5">
                  <Label>Salida proyectada</Label>
                  <Input type="datetime-local" value={form.scheduledOriginArrivalAt} onChange={(e) => setForm(f => ({ ...f, scheduledOriginArrivalAt: e.target.value }))} />
                </div>
                <div className="grid gap-1.5">
                  <Label>Llegada proyectada</Label>
                  <Input type="datetime-local" value={form.scheduledDestinationArrivalAt} onChange={(e) => setForm(f => ({ ...f, scheduledDestinationArrivalAt: e.target.value }))} />
                </div>
              </div>
            ) : (
              <>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                <Field label="Remitente"    value={shipment.senderName} />
                <Field label="Destinatario" value={shipment.recipientName} />
                <Field label="Origen"  value={[shipment.senderCityName, shipment.senderCountryName].filter(Boolean).join(" / ") || null} />
                <Field label="Destino" value={[shipment.recipientCityName, shipment.recipientCountryName].filter(Boolean).join(" / ") || null} />
                {shipment.senderAddress && <Field label="Dirección origen"  value={shipment.senderAddress} />}
                {shipment.recipientAddress && <Field label="Dirección destino" value={shipment.recipientAddress} />}
                <Field label="Salida proyectada"  value={fmtDate(shipment.scheduledOriginArrivalAt)} />
                <Field label="Llegada proyectada" value={fmtDate(shipment.scheduledDestinationArrivalAt)} />
              </dl>
              {shipment.workLine === "nacional" && (
                <div className="mt-4 grid gap-2 border-t pt-4 sm:grid-cols-2">
                  <ShipmentRouteInfoBlock
                    title="Datos de viaje"
                    origin={shipment.routeOriginCityName ?? shipment.senderCityName}
                    destination={shipment.routeName ?? shipment.routeTargetCityName ?? shipment.recipientCityName}
                    km={shipment.routeKm ?? shipment.shipmentKm}
                    code={shipment.routeCode}
                  />
                  <ShipmentRouteInfoBlock
                    title="Datos de carga"
                    origin={shipment.senderCityName}
                    destination={shipment.routeTargetCityName ?? shipment.recipientCityName}
                    km={shipment.shipmentKm}
                    code={shipment.cargoRouteCode}
                    showKm={false}
                  />
                </div>
              )}
              {nationalRouteStopRows.length > 0 && (
                <div className="mt-4 grid gap-2 border-t pt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Paradas</p>
                  <div className="space-y-2">
                    {nationalRouteStopRows.map((stop, index) => (
                      <div key={`${stop.partyId}-${index}`} className="flex items-start gap-2 text-sm">
                        <span className={cn(
                          "flex size-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold",
                          stop.kind === "origin"
                            ? "bg-emerald-100 text-emerald-700"
                            : stop.kind === "destination"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-orange-100 text-orange-700",
                        )}>
                          {stop.kind === "origin" ? "O" : stop.kind === "destination" ? "D" : index}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-medium">{stop.partyName}</span>
                          {stop.cityName && <span className="block text-xs text-muted-foreground">{stop.cityName}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
            )}
          </SectionCard>
        </div>

        {/* ── Verificaciones técnicas ── */}
        {shipment.techCheck && (
          <SectionCard
            title="Verificaciones técnicas"
            icon={<ClipboardCheckIcon className="size-4" />}
            action={isAdminOrOperator && !shipment.deletedAt ? (
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setDeleteTechCheckOpen(true)}
              >
                <Trash2Icon className="size-3 mr-1" />
                Borrar verificación
              </Button>
            ) : undefined}
          >
            {(() => {
              // Support both new array format and legacy single-object format
              const rawTc = shipment.techCheck as unknown;
              const entries = Array.isArray(rawTc) ? rawTc as {
                date:       string;
                truck:      { item: string; status: string | null }[];
                trailer:    { item: string; status: string | null }[];
                savedAt:    string;
                driverName?: string | null;
              }[] : null;

              // Legacy format (single object — old data before multi-day)
              if (!entries) {
                const tc = rawTc as { truck?: { status: string | null }[]; trailer?: { status: string | null }[]; savedAt?: string };
                const allItems = [...(tc.truck ?? []), ...(tc.trailer ?? [])];
                const okCount  = allItems.filter(i => i.status === "ok").length;
                const obsCount = allItems.filter(i => i.status === "observado").length;
                const pending  = allItems.filter(i => i.status === null).length;
                return (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground italic">Formato anterior (registro único)</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {okCount > 0 && <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-2.5 py-1 text-xs font-bold">{okCount} OK</span>}
                      {obsCount > 0 && <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2.5 py-1 text-xs font-bold">{obsCount} Observados</span>}
                      {pending > 0 && <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground px-2.5 py-1 text-xs font-bold">{pending} Sin verificar</span>}
                    </div>
                    {tc.savedAt && <p className="text-xs text-muted-foreground">Guardado el {fmtTripPlanShort(tc.savedAt)}</p>}
                  </div>
                );
              }

              // New multi-day format
              if (entries.length === 0) {
                return <p className="text-sm text-muted-foreground/60 text-center py-2">Sin verificaciones registradas.</p>;
              }

              // Sort entries by date ascending
              const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));

              return (
                <div className="space-y-0 rounded-md border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Día</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-muted-foreground">Camión</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-muted-foreground">Acoplado</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-muted-foreground hidden sm:table-cell">Observados</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground hidden md:table-cell">Registrado por</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground hidden lg:table-cell">Hora</th>
                        <th className="w-9" />
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((entry, idx) => {
                        const [y, mo, d] = entry.date.split("-").map(Number);
                        const dateLabel = (y && mo && d)
                          ? new Date(y, mo - 1, d).toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "short" })
                          : entry.date;

                        const truckItems   = entry.truck   ?? [];
                        const trailerItems = entry.trailer ?? [];
                        const truckOk    = truckItems.filter(i => i.status === "ok").length;
                        const truckTotal = truckItems.length;
                        const trailerOk    = trailerItems.filter(i => i.status === "ok").length;
                        const trailerTotal = trailerItems.length;
                        const obsCount = [...truckItems, ...trailerItems].filter(i => i.status === "observado").length;

                        const savedTime = entry.savedAt
                          ? new Date(entry.savedAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })
                          : "—";

                        return (
                          <tr key={entry.date} className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                            <td className="px-3 py-2.5 font-medium capitalize text-xs">{dateLabel}</td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`text-xs font-bold ${truckOk === truckTotal ? "text-green-600" : truckOk > 0 ? "text-amber-600" : "text-muted-foreground"}`}>
                                {truckOk}/{truckTotal}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`text-xs font-bold ${trailerOk === trailerTotal ? "text-green-600" : trailerOk > 0 ? "text-amber-600" : "text-muted-foreground"}`}>
                                {trailerOk}/{trailerTotal}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center hidden sm:table-cell">
                              {obsCount > 0
                                ? <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-bold">{obsCount} OBS</span>
                                : <span className="text-xs text-muted-foreground">—</span>
                              }
                            </td>
                            <td className="px-3 py-2.5 text-xs text-muted-foreground hidden md:table-cell">
                              {entry.driverName ?? "—"}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-muted-foreground hidden lg:table-cell">
                              {savedTime}
                            </td>
                            <td className="px-2 py-2.5 text-center">
                              <button
                                onClick={() => setTechCheckEntryDetail(entry)}
                                title="Ver detalle"
                                className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                              >
                                <EyeIcon className="size-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </SectionCard>
        )}

        {/* ── Journey milestones ── */}
        {(hasAnyJourneyTimestamp || shipment.workLine) && (
          <SectionCard
            title="Hitos del viaje"
            icon={<ClockIcon className="size-4" />}
            action={isAdminOrOperator && shipment.status === "finalizado" ? (
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs text-orange-600 border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                disabled={revertFinalizationMut.isPending}
                onClick={() => revertFinalizationMut.mutate()}
              >
                {revertFinalizationMut.isPending ? "Revirtiendo…" : "Revertir finalización"}
              </Button>
            ) : undefined}
          >
            <JourneyTimeline
              milestones={visibleMilestones}
              stages={shipment.workLine === "internacional" ? INTERNACIONAL_STAGES : undefined}
              shipment={shipment}
              onMilestoneClick={isEditing ? (m: JourneyMilestone, val: string | null) => {
                setMilestoneEdit({ key: m.key, label: m.label, value: isoToLocalDatetime(val) })
                setMilestoneError(null)
              } : undefined}
            />
          </SectionCard>
        )}

        {/* ── Trips ── */}
        <SectionCard
          title="Viajes"
          icon={<TruckIcon className="size-4" />}
        >
          <div className="flex items-center justify-between -mt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {tripsLoading ? "Cargando..." : `${tripsData.length} viaje${tripsData.length !== 1 ? "s" : ""}`}
              </span>
              {shipment.workLine === "batea" && (
                <div className="flex items-center gap-1.5">
                  <GitBranchIcon className="size-3 text-muted-foreground" />
                  <span className="text-[11px] text-muted-foreground font-medium">Multi-pierna</span>
                  {isEditing && isAdminOrOperator ? (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={form.isMultiLeg}
                      onClick={() => setForm(f => ({ ...f, isMultiLeg: !f.isMultiLeg }))}
                      className={cn(
                        "relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent",
                        "transition-colors duration-200 focus-visible:outline-none",
                        form.isMultiLeg ? "bg-blue-600" : "bg-input"
                      )}
                    >
                      <span className={cn(
                        "pointer-events-none inline-block size-3 rounded-full bg-white shadow-sm",
                        "transition-transform duration-200",
                        form.isMultiLeg ? "translate-x-3" : "translate-x-0"
                      )} />
                    </button>
                  ) : (
                    <span className={cn(
                      "relative inline-flex h-4 w-7 shrink-0 rounded-full border-2 border-transparent",
                      shipment.isMultiLeg ? "bg-blue-600" : "bg-input"
                    )}>
                      <span className={cn(
                        "pointer-events-none inline-block size-3 rounded-full bg-white shadow-sm",
                        "transition-transform duration-200",
                        shipment.isMultiLeg ? "translate-x-3" : "translate-x-0"
                      )} />
                    </span>
                  )}
                </div>
              )}
            </div>
            {can("write", "shipments") && !shipment.deletedAt && isEditing && (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={openNewTrip}>
                <PlusIcon className="size-3.5" />
                Agregar viaje
              </Button>
            )}
          </div>

          {tripsData.length === 0 && !tripsLoading ? (
            <p className="text-sm text-muted-foreground/60 text-center py-4">
              No hay viajes asignados a este envío.
            </p>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <ShipmentTripsTable
                trips={tripsData}
                {...(can("write", "shipments") && isEditing ? {
                  actions: {
                    onEdit: openEditTrip,
                    onSplit: (t: TripRow) => {
                      setSplitingTrip(t)
                      setSplitMobileId(""); setSplitCityId("")
                      setSplitDriverId(""); setSplitTruckId(""); setSplitTrailerId("")
                      setSplitOpen(true)
                    },
                    onDelete: (t: TripRow) => { setDeletingTrip(t); setTripDeleteOpen(true) },
                  },
                } : {})}
              />
            </div>
          )}
        </SectionCard>

        {/* ── Batea ── */}
        {(shipment.workLine === "batea" || (isEditing && form.workLine === "batea")) && (
          <SectionCard title="Batea" icon={<CalendarIcon className="size-4" />}>
            {isEditing ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div className="grid gap-1.5">
                  <Label>Ventana de carga</Label>
                  <Select
                    value={form.loadingWindow || "__none__"}
                    onValueChange={(v) => setForm(f => ({ ...f, loadingWindow: !v || v === "__none__" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {form.loadingWindow
                          ? (BATEA_LOADING_WINDOWS.find(w => w.slug === form.loadingWindow)?.label ?? form.loadingWindow)
                          : <span className="text-muted-foreground">Sin asignar</span>}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin asignar</SelectItem>
                      {BATEA_LOADING_WINDOWS.map(w => (
                        <SelectItem key={w.slug} value={w.slug}>{w.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Destino final</Label>
                  <Select
                    value={form.finalDestinationId || "__none__"}
                    onValueChange={(v) => setForm(f => ({ ...f, finalDestinationId: !v || v === "__none__" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {form.finalDestinationId
                          ? (finalDestinationOptions.find(d => d.id === form.finalDestinationId)?.name ?? form.finalDestinationId)
                          : <span className="text-muted-foreground">Sin asignar</span>}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin asignar</SelectItem>
                      {finalDestinationOptions.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                <Field
                  label="Ventana de carga"
                  value={shipment.loadingWindow
                    ? (BATEA_LOADING_WINDOWS.find(w => w.slug === shipment.loadingWindow)?.label ?? shipment.loadingWindow)
                    : null}
                />
                <Field
                  label="Destino final"
                  value={shipment.finalDestinationId
                    ? (finalDestinationOptions.find(d => d.id === shipment.finalDestinationId)?.name ?? shipment.finalDestinationId)
                    : null}
                />
              </dl>
            )}
          </SectionCard>
        )}

        {/* ── Plan de viaje ── */}
        {shipment.tripPlan && (
          <SectionCard title="Planificación de viaje" icon={<MapIcon className="size-4" />}>
            {(() => {
              const p = shipment.tripPlan as { restHours?: number; savedAt?: string; route?: { origen?: string; destino?: string } | null }
              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-4 text-sm">
                      {p.restHours !== undefined && (
                        <div className="flex items-center gap-1.5 rounded-lg bg-muted/50 px-3 py-2">
                          <MoonIcon className="size-3.5 text-muted-foreground" />
                          <span><span className="font-semibold">{p.restHours}</span> h descanso previo</span>
                        </div>
                      )}
                      {p.route && (
                        <div className="flex items-center gap-1.5 rounded-lg bg-muted/50 px-3 py-2">
                          <NavigationIcon className="size-3.5 text-muted-foreground" />
                          <span className="font-medium">{p.route.origen} → {p.route.destino}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isEditing && isAdminOrOperator && !shipment.deletedAt && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleteTripPlanOpen(true)}
                        >
                          <Trash2Icon className="size-3 mr-1" />
                          Borrar planificación
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setTripPlanOpen(true)}
                        className="gap-1.5 text-indigo-600 hover:text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                      >
                        <MapIcon className="size-3.5" />
                        Ver detalle
                      </Button>
                    </div>
                  </div>
                  {p.savedAt && (
                    <p className="text-xs text-muted-foreground">
                      Guardado el {fmtTripPlanShort(p.savedAt)}
                    </p>
                  )}
                </div>
              )
            })()}
          </SectionCard>
        )}

        {/* ── Logistics ── */}
        <SectionCard title="Logística" icon={<TruckIcon className="size-4" />}>
          {isEditing ? (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                <div className="grid gap-1.5">
                  <Label>N° Remito</Label>
                  <div className="flex gap-2">
                    <Input
                      value={form.deliveryNoteNumber}
                      onChange={(e) => setForm(f => ({ ...f, deliveryNoteNumber: e.target.value }))}
                      maxLength={100}
                      placeholder="—"
                      className="flex-1"
                    />
                    <Select
                      value={form.deliveryNoteStatus}
                      onValueChange={(v) => setForm(f => ({ ...f, deliveryNoteStatus: v as "normal" | "validado" | "reclamado" }))}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue>
                          <span className={cn(
                            "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                            DELIVERY_NOTE_STATUS_CLASS[form.deliveryNoteStatus as DeliveryNoteStatus],
                          )}>
                            {DELIVERY_NOTE_STATUS_LABEL[form.deliveryNoteStatus as DeliveryNoteStatus]}
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">{DELIVERY_NOTE_STATUS_LABEL.normal}</SelectItem>
                        <SelectItem value="validado">{DELIVERY_NOTE_STATUS_LABEL.validado}</SelectItem>
                        <SelectItem value="reclamado">{DELIVERY_NOTE_STATUS_LABEL.reclamado}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-1.5">
                  <Label>Peso carga (kg)</Label>
                  <Input type="number" value={form.loadingWeightKg} onChange={(e) => setForm(f => ({ ...f, loadingWeightKg: e.target.value }))} placeholder="—" min="0" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Peso descarga (kg)</Label>
                  <Input type="number" value={form.unloadingWeightKg} onChange={(e) => setForm(f => ({ ...f, unloadingWeightKg: e.target.value }))} placeholder="—" min="0" />
                </div>
              </div>

            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">N° Remito</p>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium">{shipment.deliveryNoteNumber ?? "—"}</span>
                    {shipment.deliveryNoteNumber && (
                      <span className={cn(
                        "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                        DELIVERY_NOTE_STATUS_CLASS[shipment.deliveryNoteStatus as DeliveryNoteStatus],
                      )}>
                        {DELIVERY_NOTE_STATUS_LABEL[shipment.deliveryNoteStatus as DeliveryNoteStatus]}
                      </span>
                    )}
                  </div>
                </div>
                <Field label="Peso carga (kg)"    value={shipment.loadingWeightKg ? `${shipment.loadingWeightKg} kg` : null} />
                <Field label="Peso descarga (kg)" value={shipment.unloadingWeightKg ? `${shipment.unloadingWeightKg} kg` : null} />
              </div>


            </>
          )}

          {/* Documents sub-section — visible in both view and edit mode */}
          {(shipmentDocs.length > 0 || isAdminOrOperator) && (
            <div className="border-t pt-4 space-y-3">
              <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase flex items-center gap-2">
                <FileIcon className="size-3.5" />
                Documentos de viaje
              </p>
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Archivo</TableHead>
                      <TableHead className="w-40">Tipo</TableHead>
                      <TableHead className="w-16">Letra</TableHead>
                      <TableHead>Comentario</TableHead>
                      <TableHead className="w-36">Subido por</TableHead>
                      <TableHead className="w-36">Fecha</TableHead>
                      {isEditing && <TableHead className="w-16" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shipmentDocs.map(doc => (
                      <DocRow
                        key={doc.id}
                        doc={doc}
                        editing={isEditing}
                        docTypeOptions={shipmentDocTypes}
                        onDelete={async () => {
                          await authorizedFetch(
                            `${API_URL}/api/shipments/${id}/docs/${doc.id}`,
                            { method: "DELETE" },
                          )
                          void refetchDocs()
                        }}
                        onSaveDoc={async (notes, docTypeId, letter) => {
                          await authorizedFetch(
                            `${API_URL}/api/shipments/${id}/docs/${doc.id}`,
                            {
                              method: "PATCH",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ notes, letter, ...(docTypeId && { docTypeId }) }),
                            },
                          )
                          void refetchDocs()
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Upload form — edit mode only */}
              {isEditing && isAdminOrOperator && !shipment.deletedAt && (
                <div className="flex items-center gap-2 pt-1 flex-wrap">
                  <input
                    ref={docFileInputRef}
                    type="file"
                    className="hidden"
                    onChange={e => setDocFile(e.target.files?.[0] ?? null)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => docFileInputRef.current?.click()}
                  >
                    <UploadIcon className="size-3.5 mr-1.5" />
                    {docFile ? docFile.name.slice(0, 22) + (docFile.name.length > 22 ? "…" : "") : "Subir documento"}
                  </Button>
                  <select
                    value={docTypeId}
                    onChange={e => {
                      setDocTypeId(e.target.value)
                      setDocLetter(shipmentDocTypes.find(type => type.id === e.target.value)?.control ?? "")
                    }}
                    disabled={!docFile}
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground disabled:opacity-50 shrink-0"
                  >
                    {shipmentDocTypes.length === 0 && <option value="">Remito</option>}
                    {shipmentDocTypes.map(option => (
                      <option key={option.id} value={option.id}>{option.code} — {option.name}</option>
                    ))}
                  </select>
                  <Input
                    aria-label="Letra"
                    title="Letra del documento (se toma del control del tipo)"
                    placeholder="Letra"
                    value={docLetter}
                    onChange={e => setDocLetter(e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase())}
                    maxLength={1}
                    className="h-8 w-16 text-sm text-center font-mono uppercase shrink-0"
                    disabled={!docFile}
                  />
                  <Input
                    placeholder="Comentario (opcional)"
                    value={docNote}
                    onChange={e => setDocNote(e.target.value)}
                    className="h-8 text-sm flex-1 min-w-[140px]"
                    disabled={!docFile}
                  />
                  <Button
                    size="sm"
                    disabled={!docFile || docUploading}
                    onClick={() => void handleDocUpload()}
                    className="shrink-0"
                  >
                    {docUploading ? "Subiendo…" : "Cargar"}
                  </Button>
                  {docUploadErr && (
                    <span className="text-xs text-destructive">{docUploadErr}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </SectionCard>

        {/* ── Products ── */}
        {shipment.products.length > 0 && (
          <SectionCard title="Productos" icon={<PackageIcon className="size-4" />}>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Producto</TableHead>
                    <TableHead className="w-28">Cantidad</TableHead>
                    <TableHead className="w-28">Peso (kg)</TableHead>
                    <TableHead>Notas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shipment.products.map((p, i) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div className="text-sm font-medium">{p.productName ?? "—"}</div>
                        {p.productCode && (
                          <div className="text-xs text-muted-foreground font-mono">{p.productCode}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            type="number"
                            className="h-7 text-sm w-24"
                            value={form.products[i]?.quantity ?? ""}
                            onChange={(e) => setForm(f => ({ ...f, products: f.products.map((r, j) => j === i ? { ...r, quantity: e.target.value } : r) }))}
                            placeholder="—"
                            min="0"
                          />
                        ) : (
                          <span className="text-sm tabular-nums">{p.quantity ?? <span className="text-muted-foreground/40">—</span>}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            type="number"
                            className="h-7 text-sm w-24"
                            value={form.products[i]?.weightKg ?? ""}
                            onChange={(e) => setForm(f => ({ ...f, products: f.products.map((r, j) => j === i ? { ...r, weightKg: e.target.value } : r) }))}
                            placeholder="—"
                            min="0"
                          />
                        ) : (
                          <span className="text-sm tabular-nums">{p.weightKg ?? <span className="text-muted-foreground/40">—</span>}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Input
                            className="h-7 text-sm"
                            value={form.products[i]?.notes ?? ""}
                            onChange={(e) => setForm(f => ({ ...f, products: f.products.map((r, j) => j === i ? { ...r, notes: e.target.value } : r) }))}
                            placeholder="—"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">{p.notes ?? "—"}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </SectionCard>
        )}

        {/* Section: Valorizaciones */}
        {isAdminOrOperator && (
          <SectionCard title="Valorizaciones" icon={<DollarSignIcon className="size-4" />}>
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={openCreateValorization}>
                <PlusIcon className="size-3.5 mr-1" />
                Agregar
              </Button>
            </div>

            {valorizationsLoading ? (
              <p className="text-sm text-muted-foreground text-center py-4">Cargando...</p>
            ) : valorizations.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Sin valorizaciones registradas</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead>Notas</TableHead>
                      <TableHead>Archivo</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {valorizations.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>
                          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                            {VALORIZATION_TYPE_LABELS[v.type]}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{v.quantity}</TableCell>
                        <TableCell className="text-right tabular-nums">${v.value}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[180px]">
                          <span className="line-clamp-2">{v.notes ?? "—"}</span>
                        </TableCell>
                        <TableCell>
                          {v.signedUrl ? (
                            <a
                              href={v.signedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              <FileIcon className="size-3" />
                              Ver
                              <ExternalLinkIcon className="size-3" />
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground/40">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right pr-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost" size="icon-sm"
                              onClick={() => openEditValorization(v)}
                              title="Editar"
                            >
                              <PencilIcon className="size-3.5" />
                            </Button>
                            {can("write", "shipments") && (
                              <Button
                                variant="ghost" size="icon-sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => { setDeletingValorizationId(v.id); setValorizationDeleteOpen(true) }}
                                title="Eliminar"
                              >
                                <Trash2Icon className="size-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>
        )}
      </div>

      {/* ── Valorization dialog ────────────────────────────────────────────────────────── */}
      <Dialog
        open={valorizationDialogOpen}
        onOpenChange={(open) => { if (!open) { setValorizationDialogOpen(false); setValorizationFile(null) } }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingValorization ? "Editar valorización" : "Nueva valorización"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label>Tipo <span className="text-destructive">*</span></Label>
              <Select
                value={valorizationForm.type}
                onValueChange={(v) => setValorizationForm(f => ({ ...f, type: v as ValorizationType }))}
              >
                <SelectTrigger>
                  <SelectValue>{VALORIZATION_TYPE_LABELS[valorizationForm.type]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {VALORIZATION_TYPE_VALUES.map(t => (
                    <SelectItem key={t} value={t}>{VALORIZATION_TYPE_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Cantidad <span className="text-destructive">*</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={valorizationForm.quantity}
                  onChange={(e) => setValorizationForm(f => ({ ...f, quantity: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="grid gap-1.5">
                <Label>Valor $ <span className="text-destructive">*</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={valorizationForm.value}
                  onChange={(e) => setValorizationForm(f => ({ ...f, value: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label>Notas</Label>
              <textarea
                rows={3}
                value={valorizationForm.notes}
                onChange={(e) => setValorizationForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Observaciones..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Documento (opcional)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => setValorizationFile(e.target.files?.[0] ?? null)}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadIcon className="size-3.5 mr-1" />
                  Seleccionar archivo
                </Button>
                {valorizationFile && (
                  <span className="text-xs text-muted-foreground line-clamp-1 flex-1">
                    {valorizationFile.name}
                  </span>
                )}
                {!valorizationFile && editingValorization?.fileKey && (
                  <span className="text-xs text-muted-foreground">Archivo ya cargado</span>
                )}
              </div>
            </div>
          </div>

          {valorizationFormError && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">
              {valorizationFormError}
            </p>
          )}

          <DialogFooter className="gap-2">
            <DialogClose>
              <Button variant="outline" type="button">Cancelar</Button>
            </DialogClose>
            <Button
              onClick={() => {
                if (!valorizationForm.quantity || !valorizationForm.value) {
                  setValorizationFormError("Cantidad y valor son requeridos")
                  return
                }
                setValorizationFormError(null)
                valorizationMut.mutate(valorizationForm)
              }}
              disabled={valorizationMut.isPending || valorizationUploading}
            >
              {valorizationUploading ? "Subiendo..." : valorizationMut.isPending ? "Guardando..." : editingValorization ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Valorization delete dialog ─────────────────────────────────────────────── */}
      <Dialog open={valorizationDeleteOpen} onOpenChange={setValorizationDeleteOpen}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Eliminar valorización</DialogTitle>
            <DialogDescription>Esta acción no se puede deshacer.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <DialogClose>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => deletingValorizationId && deleteValorizationMut.mutate(deletingValorizationId)}
              disabled={deleteValorizationMut.isPending}
            >
              {deleteValorizationMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Milestone dialog ──────────────────────────────────────────── */}
      <Dialog
        open={!!milestoneEdit}
        onOpenChange={(open) => { if (!open) { setMilestoneEdit(null); setMilestoneError(null) } }}
      >
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Registrar hito</DialogTitle>
            <DialogDescription>{milestoneEdit?.label}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-1.5 py-1">
            <Label>Fecha y hora</Label>
            <Input
              type="datetime-local"
              value={milestoneEdit?.value ?? ""}
              onChange={(e) => setMilestoneEdit(m => m ? { ...m, value: e.target.value } : null)}
            />
          </div>

          {milestoneError && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">
              {milestoneError}
            </p>
          )}

          <DialogFooter className="gap-2">
            {milestoneEdit?.value && (
              <Button
                variant="outline"
                className="mr-auto text-destructive hover:text-destructive"
                onClick={() => milestoneEdit && milestoneMut.mutate({ key: milestoneEdit.key, value: null })}
                disabled={milestoneMut.isPending}
              >
                Limpiar
              </Button>
            )}
            <DialogClose>
              <Button variant="outline" type="button">Cancelar</Button>
            </DialogClose>
            <Button
              onClick={() => milestoneEdit && milestoneMut.mutate({ key: milestoneEdit.key, value: milestoneEdit.value || null })}
              disabled={milestoneMut.isPending || !milestoneEdit?.value}
            >
              {milestoneMut.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Trip sheet ─────────────────────────────────────────────────── */}
      <Sheet open={tripSheetOpen} onOpenChange={(open) => { if (!open) closeTripSheet() }}>
        <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>{editingTrip ? "Editar viaje" : "Agregar viaje"}</SheetTitle>
          </SheetHeader>

          <form onSubmit={handleTripSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">

            {/* Estado */}
            <div className="grid gap-1.5">
              <Label>Estado</Label>
              <Select
                value={tripForm.status}
                onValueChange={(v) => {
                  const newStatus = v as TripStatus
                  setTripForm(f => {
                    const next: TripFormState = { ...f, status: newStatus }
                    const nowLocal = isoToLocalDatetime(new Date().toISOString())
                    if (f.status === "pendiente" && newStatus === "en_curso" && !f.departureAt) {
                      next.departureAt = nowLocal
                    }
                    if (f.status === "en_curso" && newStatus === "completado" && !f.arrivalAt) {
                      next.arrivalAt = nowLocal
                    }
                    return next
                  })
                }}
              >
                <SelectTrigger>
                  <SelectValue>{TRIP_STATUS_LABELS[tripForm.status]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {ALL_TRIP_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{TRIP_STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Recursos del viaje */}
            {editingTrip ? (
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between">
                  <Label>Recursos del viaje</Label>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => setEditResources(v => !v)}
                  >
                    {editResources ? "Cancelar cambio" : "Cambiar recursos"}
                  </button>
                </div>

                {/* Snapshot display */}
                {!editResources && (
                  <div className="rounded-md border border-input bg-muted/40 px-3 py-2.5 text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-14 shrink-0">Chofer</span>
                      <span className="font-medium">{editingTrip.driverName ?? <span className="text-muted-foreground italic">Sin chofer</span>}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-14 shrink-0">Camión</span>
                      <span className="font-medium">
                        {editingTrip.truckLicensePlate
                          ? <>{editingTrip.truckLicensePlate}{editingTrip.truckUnitNumber != null && <span className="text-muted-foreground ml-1.5">#{editingTrip.truckUnitNumber}</span>}</>
                          : <span className="text-muted-foreground italic">Sin camión</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-14 shrink-0">Semi</span>
                      <span className="font-medium">
                        {editingTrip.trailerLicensePlate
                          ? <>{editingTrip.trailerLicensePlate}{editingTrip.trailerUnitNumber != null && <span className="text-muted-foreground ml-1.5">#{editingTrip.trailerUnitNumber}</span>}</>
                          : <span className="text-muted-foreground italic">Sin semi</span>}
                      </span>
                    </div>
                  </div>
                )}

                {/* Editable selects — visible only when user clicks "Cambiar recursos" */}
                {editResources && (
                  <div className="rounded-md border border-input px-3 py-3 space-y-3">
                    <div className="grid gap-1.5">
                      <Label className="text-xs text-muted-foreground">Chofer</Label>
                      <SearchableSelect
                        options={splitDriverSelectOptions}
                        value={tripForm.driverId || "__none__"}
                        onValueChange={(v) => setTripForm(f => ({ ...f, driverId: v === "__none__" ? "" : v }))}
                        placeholder="Sin chofer"
                        searchPlaceholder="Buscar chofer..."
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-xs text-muted-foreground">Camión</Label>
                      <SearchableSelect
                        options={splitTruckSelectOptions}
                        value={tripForm.truckId || "__none__"}
                        onValueChange={(v) => setTripForm(f => ({ ...f, truckId: v === "__none__" ? "" : v }))}
                        placeholder="Sin camión"
                        searchPlaceholder="Buscar patente..."
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-xs text-muted-foreground">Semi</Label>
                      <SearchableSelect
                        options={splitTrailerSelectOptions}
                        value={tripForm.trailerId || "__none__"}
                        onValueChange={(v) => setTripForm(f => ({ ...f, trailerId: v === "__none__" ? "" : v }))}
                        placeholder="Sin semi"
                        searchPlaceholder="Buscar patente..."
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid gap-1.5">
                <Label>Móvil</Label>
                <SearchableSelect
                  options={mobileEditor.mobileSelectOptions}
                  value={tripForm.mobileId || "__none__"}
                  onValueChange={(v) => {
                    const newId = v === "__none__" ? "" : v
                    setTripForm(f => ({ ...f, mobileId: newId }))
                    mobileEditor.onMobileSelected(newId)
                  }}
                  placeholder="Sin asignar"
                  searchPlaceholder="Buscar patente o chofer..."
                />
                <MobileEditorPanel mobileId={tripForm.mobileId} editor={mobileEditor} />
              </div>
            )}

            {/* Ciudad origen */}
            <div className="grid gap-1.5">
              <Label>Ciudad de origen</Label>
              <SearchableSelect
                options={citySelectOptions}
                value={tripForm.originCityId || "__none__"}
                onValueChange={(v) => setTripForm(f => ({ ...f, originCityId: v === "__none__" ? "" : v }))}
                placeholder="Sin ciudad"
                searchPlaceholder="Buscar ciudad..."
              />
            </div>

            {/* Ciudad destino */}
            <div className="grid gap-1.5">
              <Label>Ciudad de destino</Label>
              <SearchableSelect
                options={citySelectOptions}
                value={tripForm.destinationCityId || "__none__"}
                onValueChange={(v) => setTripForm(f => ({ ...f, destinationCityId: v === "__none__" ? "" : v }))}
                placeholder="Sin ciudad"
                searchPlaceholder="Buscar ciudad..."
              />
            </div>

            {/* Salida */}
            <div className="grid gap-1.5">
              <Label>Salida real</Label>
              <Input
                type="datetime-local"
                value={tripForm.departureAt}
                onChange={(e) => setTripForm(f => ({ ...f, departureAt: e.target.value }))}
              />
            </div>

            {/* Llegada */}
            <div className="grid gap-1.5">
              <Label>Llegada real</Label>
              <Input
                type="datetime-local"
                value={tripForm.arrivalAt}
                onChange={(e) => setTripForm(f => ({ ...f, arrivalAt: e.target.value }))}
              />
            </div>

            {/* Notas */}
            <div className="grid gap-1.5">
              <Label>Notas</Label>
              <textarea
                rows={2}
                value={tripForm.notes}
                onChange={(e) => setTripForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Observaciones del viaje..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              />
            </div>

            {/* Anticipo de viaje */}
            <div className="grid gap-1.5">
              <Label>Anticipo de viaje</Label>
              <Select
                value={tripForm.hasTravelAdvance ?? "no"}
                onValueChange={(v) => setTripForm(f => ({
                  ...f,
                  hasTravelAdvance: v === "no" ? null : v as "viaje" | "gnc" | "viaje_gnc",
                }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="viaje">Viaje</SelectItem>
                  <SelectItem value="gnc">GNC</SelectItem>
                  <SelectItem value="viaje_gnc">Viaje + GNC</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {tripFormError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{tripFormError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeTripSheet}>Cancelar</Button>
              <Button type="submit" disabled={createTripMut.isPending || updateTripMut.isPending || mobileEditor.isPending}>
                {createTripMut.isPending || updateTripMut.isPending || mobileEditor.isPending
                  ? "Guardando..."
                  : editingTrip ? "Guardar cambios" : "Agregar viaje"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Mobile confirm dialog ─────────────────────────────────────────────── */}
      <MobileEditorConfirmDialog
        editor={mobileEditor}
        onConfirmed={async (driverId, trailerId) => {
          if (editingTrip) {
            // Patch the trip directly with correct values (avoids reading stale mobileOptions)
            const mobile = mobileOptions.find(m => m.id === tripForm.mobileId)
            await authorizedFetch(`${API_URL}/api/trips/${editingTrip.id}`, {
              method:  "PATCH",
              headers: { "Content-Type": "application/json" },
              body:    JSON.stringify({
                mobileId:          tripForm.mobileId          || null,
                driverId:          driverId                   || null,
                truckId:           mobile?.truckId            ?? null,
                trailerId:         trailerId                  || null,
                originCityId:      tripForm.originCityId      || null,
                destinationCityId: tripForm.destinationCityId || null,
                departureAt:       tripForm.departureAt ? new Date(tripForm.departureAt).toISOString() : null,
                arrivalAt:         tripForm.arrivalAt   ? new Date(tripForm.arrivalAt).toISOString()   : null,
                status:            tripForm.status,
                notes:             tripForm.notes || null,
              }),
            })
            invalidateTrips()
            toast.success("Viaje actualizado")
            closeTripSheet()
          } else {
            // New trip: include the confirmed driver/trailer overrides
            const mobile = mobileOptions.find(m => m.id === tripForm.mobileId)
            const res = await authorizedFetch(`${API_URL}/api/trips`, {
              method:  "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                shipmentId:        id,
                mobileId:          tripForm.mobileId          || null,
                driverId:          driverId                   || null,
                truckId:           mobile?.truckId            ?? null,
                trailerId:         trailerId                  || null,
                originCityId:      tripForm.originCityId      || null,
                destinationCityId: tripForm.destinationCityId || null,
                departureAt:       tripForm.departureAt ? new Date(tripForm.departureAt).toISOString() : null,
                arrivalAt:         tripForm.arrivalAt   ? new Date(tripForm.arrivalAt).toISOString()   : null,
                status:            tripForm.status,
                notes:             tripForm.notes || null,
              }),
            })
            if (!res.ok) {
              const b = await res.json() as { error?: string }
              setTripFormError(b.error ?? "Error al crear el viaje")
              return
            }
            invalidateTrips()
            toast.success("Viaje agregado")
            closeTripSheet()
          }
          mobileEditor.closeConfirm()
        }}
      />

      {/* ── Trip delete dialog ──────────────────────────────────────────────── */}
      <Dialog open={tripDeleteOpen} onOpenChange={setTripDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar viaje</DialogTitle>
            <DialogDescription>
              ¿Eliminás el viaje de{" "}
              <strong>{deletingTrip?.truckLicensePlate ?? "este móvil"}</strong>?
              Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTripDeleteOpen(false); setDeletingTrip(null) }}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={deleteTripMut.isPending}
              onClick={() => deletingTrip && deleteTripMut.mutate(deletingTrip.id)}
            >
              {deleteTripMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete trip plan dialog ──────────────────────────────────────────── */}
      <Dialog open={deleteTripPlanOpen} onOpenChange={setDeleteTripPlanOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Borrar planificación de viaje</DialogTitle>
            <DialogDescription>
              ¿Eliminás la planificación de viaje registrada en este envío?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTripPlanOpen(false)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={deleteTripPlanMut.isPending}
              onClick={() => deleteTripPlanMut.mutate()}
            >
              {deleteTripPlanMut.isPending ? "Eliminando..." : "Borrar planificación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete tech check dialog ─────────────────────────────────────────── */}
      <Dialog open={deleteTechCheckOpen} onOpenChange={setDeleteTechCheckOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Borrar verificación técnica</DialogTitle>
            <DialogDescription>
              ¿Eliminás todas las verificaciones técnicas registradas en este envío?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTechCheckOpen(false)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={deleteTechCheckMut.isPending}
              onClick={() => deleteTechCheckMut.mutate()}
            >
              {deleteTechCheckMut.isPending ? "Eliminando..." : "Borrar verificación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete dialog ──────────────────────────────────────────────────────── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar envío</DialogTitle>
            <DialogDescription>
              ¿Eliminás el envío <strong>#{shipment.internalId}</strong>
              {shipment.senderName ? ` — ${shipment.senderName}` : ""}?
              Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Split trip dialog ────────────────────────────────────────────── */}
      {(() => {
        function resetSplit() {
          setSplitOpen(false); setSplitingTrip(null)
          setSplitMobileId(""); setSplitCityId("")
          setSplitDriverId(""); setSplitTruckId(""); setSplitTrailerId("")
          setSplitTruckSearch(""); setSplitTruckSearchDb("")
          setSplitTrailerSearch(""); setSplitTrailerSearchDb("")
        }
        const cutCityLabel = citySelectOptions.find(c => c.value === splitCityId)?.label

        // ── Mobile impact analysis ──────────────────────────────────────────
        const tramo1Mobile = mobileOptions.find(m => m.id === splitingTrip?.mobileId)
        const tramo2Mobile = mobileOptions.find(m => m.id === splitMobileId)

        const impact = analyzeSplitImpact({
          splitDriverId, splitTruckId, splitTrailerId, splitMobileId,
          tramo1Mobile, tramo2Mobile,
        })
        const { resourcesModified, driverBelongsToTramo1, trailerBelongsToTramo1, mobileSwitchedByTruck, tramo1Label, tramo2Label } = impact

        const tramo1TrailerPlate = tramo1Mobile?.trailerId ? (unitList.find(u => u.id === tramo1Mobile.trailerId)?.licensePlate ?? null) : null
        const tramo1DriverName   = tramo1Mobile?.driverName ?? null

        return (
          <Dialog open={splitOpen} onOpenChange={(o) => { if (!o) resetSplit() }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ScissorsIcon className="size-4" />
                  Dividir viaje
                </DialogTitle>
                <DialogDescription>
                  Elegí la ciudad de corte y configurá los recursos del Tramo 2.
                </DialogDescription>
              </DialogHeader>

              {splitingTrip && (
                <div className="flex flex-col gap-4">

                  {/* Split city */}
                  <div className="grid gap-1.5">
                    <Label>Ciudad de corte <span className="text-destructive">*</span></Label>
                    <SearchableSelect
                      options={citySelectOptions}
                      value={splitCityId || "__none__"}
                      onValueChange={(v) => setSplitCityId(v === "__none__" ? "" : v)}
                      placeholder="Seleccionar ciudad..."
                      searchPlaceholder="Buscar ciudad..."
                    />
                  </div>

                  {/* Two legs preview */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border p-3 flex flex-col gap-1">
                      <p className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wide">Tramo 1</p>
                      <p className="text-sm font-medium">{splitingTrip.originCityName ?? <span className="text-muted-foreground/50 italic text-xs">Sin origen</span>}</p>
                      <div className="flex items-center gap-1">
                        <ArrowRightIcon className="size-3 text-muted-foreground" />
                        {cutCityLabel
                          ? <span className="text-sm font-medium">{cutCityLabel}</span>
                          : <span className="text-xs italic text-muted-foreground">Ciudad de corte…</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{splitingTrip.truckLicensePlate ?? "Sin móvil"}</p>
                    </div>
                    <div className="rounded-lg border p-3 flex flex-col gap-1">
                      <p className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wide">Tramo 2</p>
                      <div className="flex items-center gap-1">
                        {cutCityLabel
                          ? <span className="text-sm font-medium">{cutCityLabel}</span>
                          : <span className="text-xs italic text-muted-foreground">Ciudad de corte…</span>}
                        <ArrowRightIcon className="size-3 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium">{splitingTrip.destinationCityName ?? <span className="text-muted-foreground/50 italic text-xs">Sin destino</span>}</p>
                    </div>
                  </div>

                  {/* Mobile for leg 2 */}
                  <div className="grid gap-1.5">
                    <Label>Móvil para el Tramo 2 <span className="text-destructive">*</span></Label>
                    <SearchableSelect
                      options={splitMobileSelectOptions}
                      value={splitMobileId || "__none__"}
                      onValueChange={(v) => onSplitMobileSelected(v === "__none__" ? "" : v)}
                      placeholder="Seleccionar móvil..."
                      searchPlaceholder="Buscar por patente o chofer..."
                    />
                    {mobileSwitchedByTruck && (
                      <p className="text-[11px] text-blue-600">
                        ℹ️ Móvil actualizado a <strong>{tramo2Label}</strong> según el camión seleccionado.
                      </p>
                    )}
                  </div>

                  {/* Resources panel — shown once a mobile is selected */}
                  {splitMobileId && (
                    <div className="grid gap-3 border rounded-md p-3 bg-muted/30">
                      {/* Impact notice */}
                      {(resourcesModified || trailerBelongsToTramo1 || driverBelongsToTramo1) ? (
                        <div className="flex flex-col gap-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11px] text-amber-800">
                          <p className="font-semibold">Impacto en móviles</p>
                          {resourcesModified && (
                            <p>• El móvil <strong>{tramo2Label}</strong> será actualizado con los recursos modificados.</p>
                          )}
                          {driverBelongsToTramo1 && (
                            <p>• El chofer <strong>{tramo1DriverName ?? "seleccionado"}</strong> será desasignado del móvil <strong>{tramo1Label}</strong>.</p>
                          )}
                          {trailerBelongsToTramo1 && (
                            <p>• El tráiler <strong>{tramo1TrailerPlate ?? "seleccionado"}</strong> será desasignado del móvil <strong>{tramo1Label}</strong>.</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">
                          Ajustá los recursos del Tramo 2. Si modificás chofer, camión o tráiler, el móvil participante será actualizado.
                        </p>
                      )}

                      {/* Driver */}
                      <div className="grid gap-1.5">
                        <Label>Chofer</Label>
                        <SearchableSelect
                          options={splitDriverSelectOptions}
                          value={splitDriverId || "__none__"}
                          onValueChange={(v) => setSplitDriverId(v === "__none__" ? "" : v)}
                          placeholder="Sin chofer"
                          searchPlaceholder="Buscar chofer..."
                        />
                      </div>

                      {/* Truck */}
                      <div className="grid gap-1.5">
                        <Label>Camión</Label>
                        <SearchableSelect
                          options={splitTruckSelectOptions}
                          value={splitTruckId || "__none__"}
                          onValueChange={(v) => {
                            const newTruckId = v === "__none__" ? "" : v
                            setSplitTruckId(newTruckId)
                            // If the truck belongs to a different mobile, switch mobileId to that
                            // owner so the mobile update logic targets the correct record
                            if (newTruckId) {
                              const truckOwner = mobileOptions.find(m => m.truckId === newTruckId)
                              if (truckOwner && truckOwner.id !== splitMobileId) {
                                setSplitMobileId(truckOwner.id)
                              }
                            }
                          }}
                          onSearchChange={(v) => setSplitTruckSearch(v)}
                          placeholder="Sin camión"
                          searchPlaceholder={splitTruckSearch.length < 2 ? "Escribi 2 letras para buscar..." : "Buscando..."}
                        />
                      </div>

                      {/* Trailer */}
                      <div className="grid gap-1.5">
                        <Label>Semi / Tráiler</Label>
                        <SearchableSelect
                          options={splitTrailerSelectOptions}
                          value={splitTrailerId || "__none__"}
                          onValueChange={(v) => setSplitTrailerId(v === "__none__" ? "" : v)}
                          onSearchChange={(v) => setSplitTrailerSearch(v)}
                          placeholder="Sin semi"
                          searchPlaceholder={splitTrailerSearch.length < 2 ? "Escribi 2 letras para buscar..." : "Buscando..."}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={resetSplit}>
                  Cancelar
                </Button>
                <Button
                  disabled={!splitMobileId || !splitCityId || splitTripMut.isPending}
                  onClick={() => {
                    if (!splitingTrip) return
                    splitTripMut.mutate({
                      trip:      splitingTrip,
                      mobileId:  splitMobileId,
                      cityId:    splitCityId,
                      driverId:  splitDriverId,
                      truckId:   splitTruckId,
                      trailerId: splitTrailerId,
                    })
                  }}
                >
                  {splitTripMut.isPending ? "Dividiendo..." : "Confirmar división"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )
      })()}

      {/* ── Trip plan modal ─────────────────────────────────────────── */}
      <TripPlanModal
        open={tripPlanOpen}
        onClose={() => setTripPlanOpen(false)}
        rawPlan={shipment.tripPlan}
        internalId={shipment.internalId}
      />

      {/* ── Tech check modal ────────────────────────────────────────── */}
      <TechCheckModal
        open={techCheckOpen}
        onClose={() => setTechCheckOpen(false)}
        rawCheck={shipment.techCheck}
        internalId={shipment.internalId}
      />

      {/* ── Tech check entry detail popup ───────────────────────────── */}
      {techCheckEntryDetail && (() => {
        const e = techCheckEntryDetail
        const [y, mo, d] = e.date.split("-").map(Number)
        const dateLabel = (y && mo && d)
          ? new Date(y, mo - 1, d).toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" })
          : e.date
        const allItems = [...(e.truck ?? []), ...(e.trailer ?? [])]
        const obsCount = allItems.filter(i => i.status === "observado").length
        const savedTime = e.savedAt
          ? new Date(e.savedAt).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
          : null
        return (
          <Dialog open onOpenChange={v => { if (!v) setTechCheckEntryDetail(null) }}>
            <DialogContent className="max-w-lg p-0 gap-0 flex flex-col max-h-[85vh]">
              <DialogHeader className="px-5 pt-5 pb-4 border-b shrink-0">
                <DialogTitle className="flex items-center gap-2 text-base capitalize">
                  <ClipboardCheckIcon className="h-4 w-4 text-blue-600 shrink-0" />
                  {dateLabel}
                  {obsCount > 0 && (
                    <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-bold ml-1">
                      {obsCount} OBS
                    </span>
                  )}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {e.driverName && <span className="font-medium text-foreground">{e.driverName}</span>}
                  {e.driverName && savedTime && " · "}
                  {savedTime && `Guardado el ${savedTime}`}
                </p>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {/* Truck */}
                {e.truck.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      <TruckIcon className="size-3" /> Camión
                    </p>
                    <div className="space-y-0.5">
                      {e.truck.map((item, j) => (
                        <div key={j} className="flex items-center gap-2 text-xs py-1.5 border-b last:border-0">
                          {item.status === "ok"
                            ? <CheckCircle2Icon className="h-3.5 w-3.5 text-green-500 shrink-0" />
                            : item.status === "observado"
                            ? <XCircleIcon className="h-3.5 w-3.5 text-red-500 shrink-0" />
                            : <span className="h-3.5 w-3.5 shrink-0 inline-block rounded-full border border-muted-foreground/30" />}
                          <span className="flex-1">{item.item}</span>
                          {item.status === "observado" && (
                            <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Observado</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Trailer */}
                {e.trailer.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      <PackageIcon className="size-3" /> Acoplado
                    </p>
                    <div className="space-y-0.5">
                      {e.trailer.map((item, j) => (
                        <div key={j} className="flex items-center gap-2 text-xs py-1.5 border-b last:border-0">
                          {item.status === "ok"
                            ? <CheckCircle2Icon className="h-3.5 w-3.5 text-green-500 shrink-0" />
                            : item.status === "observado"
                            ? <XCircleIcon className="h-3.5 w-3.5 text-red-500 shrink-0" />
                            : <span className="h-3.5 w-3.5 shrink-0 inline-block rounded-full border border-muted-foreground/30" />}
                          <span className="flex-1">{item.item}</span>
                          {item.status === "observado" && (
                            <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Observado</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )
      })()}

      <Dialog open={internationalIdsConfirmOpen} onOpenChange={setInternationalIdsConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar IDs internacionales</DialogTitle>
            <DialogDescription>
              Se eliminarán IDs internacionales existentes. Esta operación es destructiva y los valores no podrán reutilizarse. ¿Deseás continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Cancelar</DialogClose>
            <Button onClick={() => {
              if (pendingShipmentSave) updateMut.mutate(pendingShipmentSave)
              setPendingShipmentSave(null)
              setInternationalIdsConfirmOpen(false)
            }}>
              Confirmar eliminación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
